import { useCallback, useState } from "react";

import { inputModelToViewModelConverter, viewModelToInputModelConverter } from "@/components/features/_model/converter";

import { serverModelToInputModel } from "./converter/server-model-to-input-model";
import { useSnackSettingsFormModelMethods } from "./methods";
import { excludeKeys, type DesignSnackSettingsFormModel, type ExcludeKeys, type ServerModel } from "./type";
import { snackSettingsInputZodSchema } from "./validation-schema";

import type { SnackFormBaseModelMethodsType } from "./methods";
import type { SnackSettingsZodSchemaType } from "./validation-schema";

type Props = {
	data: ServerModel;
};

type ViewModel = DesignSnackSettingsFormModel["ViewModel"];
type InputModel = DesignSnackSettingsFormModel["InputModel"];
type QueryData = DesignSnackSettingsFormModel["QueryData"];
export type DesignSnackSettingsFormModelOnSetViewModelType = <K extends keyof ViewModel>(
	values: { key: K; value: ViewModel[K] }[],
) => void;

export type SnackFormBaseModelType = {
	snackSettingsViewModel: DesignSnackSettingsFormModel["ViewModel"];
	resetSnackSettingsViewModel: () => void;
	snackSettingsMethods: SnackFormBaseModelMethodsType;
};

export const useCreateSnackSettingsFormModel = ({ data }: Props): SnackFormBaseModelType => {
	const inputModel = serverModelToInputModel({ snackData: data });

	const initialViewModel = inputModelToViewModelConverter<
		InputModel,
		ExcludeKeys,
		QueryData,
		SnackSettingsZodSchemaType
	>({
		defaultInput: inputModel,
		initInput: undefined,
		currentInput: inputModel,
		excludeKeys,
		zodSchema: snackSettingsInputZodSchema,
	});

	const [viewModel, setViewModel] = useState<ViewModel>(initialViewModel);

	const onSetViewModel: DesignSnackSettingsFormModelOnSetViewModelType = useCallback(
		(values) => {
			const mergedViewModel = {
				...viewModel,
				...values.reduce((acc, cur) => {
					acc[cur.key] = cur.value;
					return acc;
				}, {} as ViewModel),
			} as ViewModel;

			const convertedInputModel = viewModelToInputModelConverter<InputModel, ExcludeKeys, QueryData>(
				mergedViewModel,
			);

			const convertedViewModel = inputModelToViewModelConverter<
				InputModel,
				ExcludeKeys,
				QueryData,
				SnackSettingsZodSchemaType
			>({
				defaultInput: inputModel,
				initInput: inputModel,
				currentInput: convertedInputModel,
				excludeKeys,
				zodSchema: snackSettingsInputZodSchema,
			});

			setViewModel(convertedViewModel);
		},
		[viewModel, inputModel, setViewModel],
	);

	const resetViewModel = useCallback(() => {
		setViewModel({ ...viewModel, isDirty: false });
	}, [viewModel]);

	// const onUpdate = useCallback(async () => {
	// 	await _onUpdate({ viewModel });
	// 	resetViewModel();
	// }, [_onUpdate, resetViewModel, viewModel]);

	return {
		snackSettingsViewModel: viewModel,
		resetSnackSettingsViewModel: resetViewModel,
		snackSettingsMethods: {
			...useSnackSettingsFormModelMethods({
				viewModel,
				onSetViewModel,
				snackList: data.snackList,
				savedGuestPageVisibility: data.guestPageVisibilitySettings.snackVisibility,
			}),
		},
	};
};
